/**
 * Parser for the NWS Coastal Waters Forecast (product type CWF).
 *
 * The product is plain text but reliably structured: zone blocks separated by
 * `$$`, each opening with one or more `PZZnnn-` identifiers and a purge time,
 * then the zone name, then any active headlines wrapped in `...`, then forecast
 * periods introduced by `.PERIOD...`.
 *
 * Shape of one block:
 *
 *   PZZ545-560-020430-
 *   Coastal Waters from Point Reyes to Pigeon Point California out to 10 NM-
 *   909 AM PDT Sat Aug 1 2026
 *
 *   ...SMALL CRAFT ADVISORY IN EFFECT UNTIL 9 AM PDT THIS MORNING...
 *
 *   .TODAY...NW wind 20 to 25 kt, rising to 25 to 30 kt with gusts up
 *   to 40 kt this afternoon.
 */

export interface ForecastPeriod {
  /** e.g. "TODAY", "TONIGHT", "SUN" */
  name: string;
  text: string;
}

/**
 * A named block appended to a zone forecast, e.g.
 *
 *   .....SAN FRANCISCO BAR/FOURFATHOM BANK FORECAST.....
 *
 *   IN THE DEEP WATER CHANNEL...Mixed seas of 5 to 7 ft at 10 seconds.
 *
 *   ACROSS THE BAR...Swell can be more than twice as high across the bar...
 *
 * Its sections are headed like periods but carry no leading dot and name a
 * place rather than a time, so they cannot go in `periods`.
 */
export interface ForecastSupplement {
  /** e.g. "SAN FRANCISCO BAR/FOURFATHOM BANK FORECAST" */
  title: string;
  sections: ForecastPeriod[];
}

export interface ZoneForecast {
  /** Every zone this block speaks for — the header can list several. */
  zones: string[];
  name: string;
  /** Headlines such as "GALE WARNING IN EFFECT THROUGH LATE SUNDAY NIGHT". */
  headlines: string[];
  periods: ForecastPeriod[];
  supplements: ForecastSupplement[];
}

export interface CoastalForecast {
  /** The area-wide synopsis, which has no zone of its own. */
  synopsis: string;
  zones: ZoneForecast[];
}

/**
 * UGC zone header, e.g. `PZZ545-560-020430-` or `PZZ530>535-020430-`.
 *
 * The three-letter prefix is stated once and later zones are bare numbers, so
 * `PZZ545-560-` is PZZ545 *and* PZZ560 — not one zone. `>` marks an inclusive
 * range. The trailing six digits are the purge time, not a zone.
 */
const ZONE_HEADER = /^([A-Z]{3}\d{3}(?:[->]\d{3})*)-(\d{6})-\s*$/;

export function expandZoneHeader(header: string): string[] {
  const prefix = header.slice(0, 3);
  const zones: string[] = [];
  let previous: number | null = null;

  // Each token is a zone number, optionally preceded by the separator that
  // introduced it: "PZZ545-560>565" -> "545", "-560", ">565".
  const parts = header.match(/[->]?\d{3}/g) ?? [];
  for (const part of parts) {
    const isRange = part.startsWith('>');
    const n = Number(part.replace(/^[->]/, ''));

    if (isRange && previous !== null) {
      for (let v = previous + 1; v <= n; v++) zones.push(prefix + String(v).padStart(3, '0'));
    } else {
      zones.push(prefix + String(n).padStart(3, '0'));
    }
    previous = n;
  }

  return zones;
}

/** Collapses the product's hard wrapping back into sentences. */
function unwrap(lines: string[]): string {
  return lines.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * A supplement heading: `.....SAN FRANCISCO BAR/FOURFATHOM BANK FORECAST.....`
 *
 * Four dots or more, which is what keeps it clear of the three-dot headline
 * form — `...GALE WARNING IN EFFECT...` must not be read as a supplement.
 */
const SUPPLEMENT_TITLE = /^\.{4,}\s*(.+?)\s*\.{4,}$/;

/** `ACROSS THE BAR...Swell can be...` — like a period, but with no leading dot. */
const SUPPLEMENT_SECTION = /^([A-Z][A-Z0-9 /&'()-]*?)\.\.\.(.*)$/;

function parseSupplementSections(lines: string[]): ForecastPeriod[] {
  const out: ForecastPeriod[] = [];
  let name: string | null = null;
  let buffer: string[] = [];

  const commit = () => {
    if (name !== null) out.push({ name, text: unwrap(buffer) });
    buffer = [];
  };

  for (const raw of lines) {
    const l = raw.trim();
    if (l === '') continue;

    const m = SUPPLEMENT_SECTION.exec(l);
    if (m) {
      commit();
      name = m[1].trim();
      buffer = m[2] ? [m[2]] : [];
      continue;
    }
    if (name !== null) buffer.push(l);
  }
  commit();

  return out;
}

function parseSupplements(lines: string[]): ForecastSupplement[] {
  const out: ForecastSupplement[] = [];
  let title: string | null = null;
  let buffer: string[] = [];

  const commit = () => {
    if (title !== null) out.push({ title, sections: parseSupplementSections(buffer) });
    buffer = [];
  };

  for (const raw of lines) {
    const m = SUPPLEMENT_TITLE.exec(raw.trim());
    if (m) {
      commit();
      title = m[1].trim();
      continue;
    }
    if (title !== null) buffer.push(raw);
  }
  commit();

  return out;
}

function parseBlock(block: string): ZoneForecast | null {
  const lines = block.split('\n');

  let headerAt = -1;
  let zones: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const m = ZONE_HEADER.exec(lines[i].trim());
    if (m) {
      headerAt = i;
      zones = expandZoneHeader(m[1]);
      break;
    }
  }
  if (headerAt === -1) return null;

  // The name follows the header, may wrap, and ends with a trailing hyphen.
  // A timestamp line ("909 AM PDT Sat Aug 1 2026") closes it.
  const nameLines: string[] = [];
  let i = headerAt + 1;
  for (; i < lines.length; i++) {
    const l = lines[i].trim();
    if (l === '') continue;
    if (/^\d{1,4}\s*(AM|PM)\s/i.test(l)) break;
    nameLines.push(l);
    if (l.endsWith('-')) { i++; break; }
  }
  const name = unwrap(nameLines).replace(/-$/, '').trim();

  const headlines: string[] = [];
  const periods: ForecastPeriod[] = [];
  let current: { name: string; lines: string[] } | null = null;

  const flush = () => {
    if (current) {
      periods.push({ name: current.name, text: unwrap(current.lines) });
      current = null;
    }
  };

  // Supplements are split off before periods are read. A supplement heading is
  // not a period, so the open period would otherwise absorb the heading and the
  // whole bar forecast as continuation lines — which is exactly what it did.
  const body = lines.slice(i);
  const supplementAt = body.findIndex((l) => SUPPLEMENT_TITLE.test(l.trim()));
  const periodLines = supplementAt === -1 ? body : body.slice(0, supplementAt);
  const supplements =
    supplementAt === -1 ? [] : parseSupplements(body.slice(supplementAt));

  for (let j = 0; j < periodLines.length; j++) {
    const raw = periodLines[j];
    const l = raw.trim();
    if (l === '') continue;

    const period = /^\.([A-Z0-9 ]+?)\.\.\.(.*)$/.exec(l);
    if (period) {
      flush();
      current = { name: period[1].trim(), lines: period[2] ? [period[2]] : [] };
      continue;
    }

    // Headlines are wrapped in ellipses and may span lines.
    if (l.startsWith('...') && !current) {
      const parts = [l];
      while (
        !parts[parts.length - 1].trimEnd().endsWith('...') &&
        j + 1 < periodLines.length
      ) {
        parts.push(periodLines[++j].trim());
      }
      headlines.push(unwrap(parts).replace(/^\.{3}|\.{3}$/g, '').trim());
      continue;
    }

    if (current) current.lines.push(l);
  }
  flush();

  return { zones, name, headlines, periods, supplements };
}

export function parseCoastalForecast(text: string): CoastalForecast {
  const blocks = text.split('$$').map((b) => b.trim()).filter(Boolean);

  let synopsis = '';
  const zones: ZoneForecast[] = [];

  for (const block of blocks) {
    const parsed = parseBlock(block);
    if (!parsed) continue;

    // The synopsis block carries a `.Synopsis...` pseudo-period and no forecast.
    const syn = parsed.periods.find((p) => /^SYNOPSIS/i.test(p.name));
    if (syn && parsed.periods.length === 1) {
      synopsis = syn.text;
      continue;
    }

    // A block with a name but no content is a header, not a forecast.
    if (
      parsed.periods.length === 0 &&
      parsed.headlines.length === 0 &&
      parsed.supplements.length === 0
    ) {
      continue;
    }
    zones.push(parsed);
  }

  if (!synopsis) synopsis = extractSynopsis(text);

  return { synopsis, zones };
}

/**
 * The synopsis heading is mixed case, so the period regex misses it, and the
 * heading itself contains ellipses ("...Monterey Bay...Greater Farallones...")
 * so a regex stopping at the first `...` swallows part of the title as body.
 *
 * The heading instead runs from `.Synopsis` to the first line *ending* in `...`;
 * everything after that, up to the block terminator, is the forecast.
 */
function extractSynopsis(text: string): string {
  const block = text.split('$$')[0] ?? '';
  const lines = block.split('\n');

  const start = lines.findIndex((l) => /^\.Synopsis/i.test(l.trim()));
  if (start === -1) return '';

  let bodyAt = -1;
  for (let i = start; i < lines.length; i++) {
    if (lines[i].trimEnd().endsWith('...')) {
      bodyAt = i + 1;
      break;
    }
  }
  if (bodyAt === -1) return '';

  return unwrap(lines.slice(bodyAt).map((l) => l.trim()).filter(Boolean));
}
